// apps/client/src/hooks/reports/useUpsertLoanAnnotation.ts
// Mutation hook for marking a loan as construction / excluded / etc. on the variance report.

import { useCallback, useState } from 'react';
import { httpsCallable } from 'firebase/functions';
import { functions } from '../../lib/firebase';

export type LoanAnnotationClassification =
  | 'construction'
  | 'split-tranche'
  | 'data-error'
  | 'other'
  | null;

export interface UpsertLoanAnnotationInput {
  companyId: string;
  loanId: string;
  excludedFromVariance: boolean;
  classifiedAs: LoanAnnotationClassification;
  reason: string;
}

export interface LoanAnnotation {
  loanId: string;
  excludedFromVariance: boolean;
  classifiedAs: LoanAnnotationClassification;
  reason: string;
  setBy: string;
  setAt: string;
}

export interface UseUpsertLoanAnnotationReturn {
  upsert: (input: UpsertLoanAnnotationInput) => Promise<LoanAnnotation>;
  loading: boolean;
  error: string | null;
}

/**
 * Calls upsertLoanAnnotation. Caller is responsible for triggering a refetch of the variance
 * data on success (e.g. via the refetch callback returned from useApprovedVsSettledVariance).
 */
export function useUpsertLoanAnnotation(): UseUpsertLoanAnnotationReturn {
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const upsert = useCallback(async (input: UpsertLoanAnnotationInput): Promise<LoanAnnotation> => {
    setLoading(true);
    setError(null);
    try {
      const callable = httpsCallable<UpsertLoanAnnotationInput, LoanAnnotation>(
        functions,
        'upsertLoanAnnotation',
      );
      const result = await callable(input);
      setLoading(false);
      return result.data;
    } catch (err: any) {
      const msg = err?.message || 'Failed to save annotation';
      setError(msg);
      setLoading(false);
      throw err;
    }
  }, []);

  return { upsert, loading, error };
}
